import React from 'react';
import { ArrowRight, ArrowLeft, Shield, Building2, Lock, Loader2, Gift } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

const PaymentPriceSummary = ({
  tripAmount,
  extrasAmount,
  discount = 0,
  agreements,
  onAgreementChange,
  onBack,
  onPay,
  canPay,
  isProcessing,
  paymentMethod = 'cash', // Add payment method to conditionally show/hide pay button
  autoIncludedExtras = [],
}) => {
  // Round all amounts to 2 decimal places for display
  const formatPrice = (price) => {
    return (Math.round(price * 100) / 100).toFixed(2);
  };
  
  const total = tripAmount + extrasAmount - discount;

  const allAgreed = agreements.terms && agreements.privacy && agreements.refund;
  
  // Hide pay button for PayPal since payment is handled via PayPal SDK buttons
  const showPayButton = paymentMethod !== 'paypal';

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="payment-price-summary">
      {/* Orange top accent */}
      <div className="h-1 bg-[#FF8C00]" />

      <div className="p-6">
        <h3 className="font-bold text-black text-lg mb-4">Final Amount</h3>

        {/* Price Breakdown */}
        <div className="space-y-3 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Trip</span>
            <span className="text-sm font-medium text-black">€{formatPrice(tripAmount)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Extras</span>
            <span className={`text-sm font-medium ${extrasAmount > 0 ? 'text-black' : 'text-gray-400'}`}>
              €{formatPrice(extrasAmount)}
            </span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between items-center text-green-600">
              <span className="text-sm">Discount</span>
              <span className="text-sm font-medium">-€{formatPrice(discount)}</span>
            </div>
          )}
          <div className="border-t border-gray-100 pt-3">
            <div className="flex justify-between items-center">
              <span className="text-base font-semibold text-black">Total</span>
              <span className="text-2xl font-bold text-black font-display">€{formatPrice(total)}</span>
            </div>
          </div>
        </div>

        {/* Included Services (Auto-included / Gifts) */}
        {autoIncludedExtras.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-1.5 mb-2">
              <Gift className="h-3.5 w-3.5 text-green-600" />
              <p className="text-xs text-green-700 uppercase tracking-wider font-semibold">Included Free</p>
            </div>
            <ul className="space-y-1">
              {autoIncludedExtras.map((extra) => (
                <li key={extra.id || extra.code} className="text-sm text-green-700 flex justify-between items-center">
                  <span>{extra.name || extra.label}</span>
                  <span className="flex items-center gap-1">
                    <span className="text-gray-400 line-through text-xs">€{extra.originalPrice || 15}</span>
                    <span className="font-semibold text-green-600">FREE</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Free Benefits - Checkout Reassurance */}
        <div className="mb-4 space-y-2">
          <div className="flex items-center gap-2 text-green-600 bg-green-50 rounded-lg py-2 px-3">
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="text-xs font-semibold">Free Cancellation up to 24h before</span>
          </div>
          <div className="flex items-center gap-2 text-green-600 bg-green-50 rounded-lg py-2 px-3">
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
            <span className="text-xs font-semibold">Free Meet & Greet Included (€15 value)</span>
          </div>
        </div>

        {/* Legal Checkboxes */}
        <div className="space-y-3 mb-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-3">
            Required Agreements
          </p>

          <div className="flex items-start gap-3">
            <Checkbox
              id="terms"
              checked={agreements.terms}
              onCheckedChange={(checked) => onAgreementChange('terms', checked)}
              className="mt-0.5 data-[state=checked]:bg-[#FF8C00] data-[state=checked]:border-[#FF8C00]"
              data-testid="checkbox-terms"
            />
            <label htmlFor="terms" className="text-sm text-gray-600 leading-tight cursor-pointer">
              I agree to the{' '}
              <a href="/terms-of-service/" target="_blank" rel="noopener noreferrer" className="text-[#FF8C00] hover:underline">
                Terms & Conditions
              </a>
            </label>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="privacy"
              checked={agreements.privacy}
              onCheckedChange={(checked) => onAgreementChange('privacy', checked)}
              className="mt-0.5 data-[state=checked]:bg-[#FF8C00] data-[state=checked]:border-[#FF8C00]"
              data-testid="checkbox-privacy"
            />
            <label htmlFor="privacy" className="text-sm text-gray-600 leading-tight cursor-pointer">
              I have read the{' '}
              <a href="/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-[#FF8C00] hover:underline">
                Privacy Policy
              </a>
            </label>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="refund"
              checked={agreements.refund}
              onCheckedChange={(checked) => onAgreementChange('refund', checked)}
              className="mt-0.5 data-[state=checked]:bg-[#FF8C00] data-[state=checked]:border-[#FF8C00]"
              data-testid="checkbox-refund"
            />
            <label htmlFor="refund" className="text-sm text-gray-600 leading-tight cursor-pointer">
              I understand the{' '}
              <a href="/cancellation-refund-policy/" target="_blank" rel="noopener noreferrer" className="text-[#FF8C00] hover:underline">
                Refund & Cancellation Policy
              </a>
            </label>
          </div>
        </div>

        {/* Buttons */}
        <div className="space-y-3">
          {showPayButton ? (
            <Button
              data-testid="pay-btn"
              className={`w-full h-14 text-base font-semibold transition-all duration-200 ${
                canPay && allAgreed && !isProcessing
                  ? 'bg-[#FF8C00] text-white hover:bg-[#E67E00] active:scale-[0.98]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              onClick={onPay}
              disabled={!canPay || !allAgreed || isProcessing}
            >
              {isProcessing ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Processing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Pay Securely
                </span>
              )}
            </Button>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 text-center">
                👆 Use the PayPal button above to complete your payment
              </p>
            </div>
          )}

          <Button
            data-testid="payment-back-btn"
            variant="outline"
            className="w-full border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900 h-10"
            onClick={onBack}
            disabled={isProcessing}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Trust Strip */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-3 gap-2">
            <div className="flex flex-col items-center text-center">
              <Shield className="h-4 w-4 text-[#FF8C00] mb-1" />
              <span className="text-[10px] text-gray-500">Buyer Protection</span>
            </div>
            <div className="flex flex-col items-center text-center">
              <Building2 className="h-4 w-4 text-[#FF8C00] mb-1" />
              <span className="text-[10px] text-gray-500">UK-based</span>
            </div>
            <div className="flex flex-col items-center text-center">
              <Lock className="h-4 w-4 text-[#FF8C00] mb-1" />
              <span className="text-[10px] text-gray-500">SSL Secure</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPriceSummary;
